import { Box, Typography, useTheme } from "@mui/material";
import { toPng } from "html-to-image";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import DownloadComponentButton from "../button/DownloadComponentButton";
export const sustainabilityGreens = [
  "rgba(169, 169, 169, 0.7)", // Grey
  "rgba(135, 108, 46, 0.7)", // Brown-Green
  "rgba(101, 157, 51, 0.7)", // Brand Green (#659d33)
  "rgba(112, 128, 144, 0.7)", // Grey
  "rgba(160, 82, 45, 0.7)", // Brown
  "rgba(50, 205, 50, 0.7)", // Green
  "rgba(128, 128, 128, 0.7)", // Grey
  "rgba(139, 69, 19, 0.7)", // Brown
  "rgba(34, 139, 34, 0.7)", // Green
];
export const getColors = (index) => sustainabilityGreens[index % sustainabilityGreens.length];

const DoughnutChart = forwardRef(({ data, filename, bottomLabel, options }, parentRef) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [visibility, setVisibility] = useState(false);
  const [denominator, setDenominator] = useState(1);
  const ref = useRef(null);
  useEffect(() => data && data.datasets && data.datasets.length > 0 && data.datasets[0].data && setDenominator(data.datasets[0].data.reduce((acc, val) => acc + val, 0)), [data]);
  useImperativeHandle(parentRef, () => ({
    getScreenshot: async () => {
      if (ref.current) {
        return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff" });
      }
      return null;
    },
  }));
  return (
    <Box
      onMouseEnter={() => setVisibility(true)}
      onMouseLeave={() => setVisibility(false)}
      style={{ position: "relative" }}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}>
      <Box style={{ position: "absolute", top: "10px", right: "10px" }}>
        <DownloadComponentButton ref={ref} filename={filename} visibility={visibility} />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          width: "100%"
        }}>
        <Box
          ref={ref}
          sx={{
            backgroundColor: isDark ? "#1c1c1e" : "white",
            padding: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            maxWidth: "600px"
          }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              height: { xs: "300px", md: "350px" },
              mb: 4
            }}>
            {data && <Doughnut data={data} options={{ ...options, maintainAspectRatio: false }} />}
          </Box>
          <Box sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 2,
            width: "100%"
          }}>
            {data &&
              data.labels?.map((label, index) => {
                let renamedLabel = label;
                if (label === "Product") renamedLabel = "A1 - A3";
                if (label === "Construction") renamedLabel = "A4 - A5";
                if (label === "Use") renamedLabel = "B";
                if (label === "End-of-life") renamedLabel = "C";
                if (label === "Benefits") renamedLabel = "D";
                const value = data.datasets[0].data[index];
                if (isNaN(value)) {
                  value = 0;
                }
                const percentage = isNaN((value / denominator) * 100) ? "0" : ((value / denominator) * 100).toFixed(2);
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: "fit-content",
                      fontFamily: "Poppins",
                      fontSize: "13px"
                    }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: data.datasets[0].backgroundColor[index],
                        flexShrink: 0
                      }} />
                    <Typography sx={{ fontSize: "12px", color: isDark ? "#f1f5f9" : "#4b5563" }}>
                      {renamedLabel}: <span style={{ fontWeight: 700 }}>{value > 1 ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value != 0 ? value.toPrecision(2) : 0}</span> [{percentage}%]
                    </Typography>
                  </Box>
                );
              })}
          </Box>
          {bottomLabel && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 500, fontFamily: "Poppins", color: isDark ? "#94a3b8" : "#94a3b8", textAlign: "center" }}>
                {t(bottomLabel)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default DoughnutChart;
